import { determineCardSize } from '@aviv-classified-card/card/full';
import { useThorView } from '@aviv-dxp/react-thor';
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { useLocation } from '@aviv-unified-frontend/runtime/router';
import { useBrandConfiguration } from '@search/core/brands/context';
import { Order } from '@search/core/brands/models/Order';
import { useStageConfiguration } from '@search/core/hooks/useStageConfiguration';
import { getAbTestVariant } from '@search/core/tracking/getAbTestVariant/getAbTestVariant';
import { infosEstateType, infosOrder, infosSerpPageView, getMediumInQueryString } from '@search/core/tracking/helpers';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useAppContext } from '../../contexts/AppContext/useAppContext';
import { setSearchType } from './setSearchType';
const getUserInfo = () => {
    const accessToken = Cookies.get('oauth.access.token');
    if (!accessToken || isEmpty(accessToken)) {
        return { userStatus: 'unlogged', userId: undefined };
    }
    try {
        const parsedToken = jwtDecode(accessToken);
        const userId = parsedToken?.sub;
        return { userStatus: 'logged', userId };
    }
    catch {
        return { userStatus: 'unlogged', userId: undefined };
    }
};
export const useTracking = () => {
    const { pathname, search } = useLocation();
    const { userId, userStatus } = useMemo(() => getUserInfo(), []);
    const { brand, tracking: { avSite, getTrackingLegacy, abTestFeatureFlag }, } = useBrandConfiguration();
    const [searchModel, order, pageData, classifieds, classifiedsData, enrichedClassifiedsData, featureFlags, searchIndex, tracking, forceXxlTopPosition,] = useAppContext((store) => [
        store.searchModel,
        store.order,
        store.pageData,
        store.classifieds,
        store.classifiedsData,
        store.enrichedClassifiedsData,
        store.featureFlags,
        store.searchIndex,
        store.appConfig.tracking,
        store.featureFlags?.[`abtest-serp-${brand}-force-xxl-top-position`] === true,
    ]);
    const { language } = useLocale();
    const { stage } = useStageConfiguration();
    const triggerView = useThorView();
    useEffect(() => {
        if (!pageData || !searchModel)
            return;
        const medium = getMediumInQueryString(search);
        const { locations } = pageData;
        const { priceMin, priceMax, numberOfRoomsMin, numberOfRoomsMax, distributionTypes, estateTypes } = searchModel;
        const { initiative } = tracking;
        const abTestVariant = getAbTestVariant({ abTestFeatureFlag, featureFlags });
        const getPromotionType = (id, index) => {
            const isCardXXL = index === 0 && forceXxlTopPosition && order === Order.Relevancy && enrichedClassifiedsData[id]?.size === 'XL';
            const size = determineCardSize({
                isCardXXL,
                card: classifiedsData[id],
                showContacts: false,
                enrichedData: enrichedClassifiedsData,
            });
            return `s_${size}`;
        };
        const viewProps = {
            av_url_path: pathname,
            av_category: 'classified',
            av_platform: 'web',
            av_initiative: initiative,
            av_language: language,
            av_environment: stage,
            av_pagetitle: infosSerpPageView[distributionTypes[0]]?.title,
            av_distribution_type_global: infosSerpPageView[distributionTypes[0]]?.distributionTypeGlobal,
            av_site: avSite,
            av_estate_type_global: estateTypes && infosEstateType[estateTypes[0]],
            ...(abTestVariant ? { av_ab_test_variant: abTestVariant } : {}),
            ...(medium && { av_medium: medium }),
            av_search: {
                location: locations.join('|'),
                price_max: priceMax && `${priceMax}`,
                price_min: priceMin && `${priceMin}`,
                room_nb_min: numberOfRoomsMin && `${numberOfRoomsMin}`,
                room_nb_max: numberOfRoomsMax && `${numberOfRoomsMax}`,
                search_type: setSearchType(searchModel),
                sorting: infosOrder[order],
            },
            av_items: classifieds
                // FIXME: emerged after migration
                .filter((id) => classifiedsData && classifiedsData[id] && classifiedsData[id].tracking)
                .map((id, index) => ({
                // @ts-expect-error -- FIXME: emerged after migration
                ...classifiedsData[id].tracking,
                promotion_type: getPromotionType(id, index),
                index,
                list_name: 'classified_search_results',
            })),
            av_user: {
                uid: userId,
                status: userStatus,
            },
        };
        const trackingLegacy = getTrackingLegacy({
            site: avSite,
            language,
            url_path: pathname,
            environment: stage,
            medium: medium || '',
            classifiedIds: classifieds,
            classifiedsData,
            userId,
            userStatus,
        });
        const isGA3Compatible = !!trackingLegacy;
        triggerView({
            ...(trackingLegacy ? { viewProps: trackingLegacy, additionalProps: viewProps } : { viewProps }),
            compatibilities: { GA3: isGA3Compatible, GA4: true },
        });
    }, [searchIndex]);
};
